import { isDefined, isEmpty, isNonEmpty, isNotDefined } from '@sgme/fp';
import classnames from 'classnames';
import { OnKeyDownArg } from 'components/common-form/builder/form/node/field/Grid/Keys';
import { BatchNavigationContext } from 'components/common-form/contexts/batch/BatchNavigationContext';
import { useSettlementBlackoutDates } from 'components/common-form/hooks/use-settlement-blackout-dates';
import { useBlockOrFieldReadOnly } from 'components/common-form/hooks/useBlockOrFieldReadOnly';
import { useData } from 'components/common-form/hooks/useData';
import { useExpiryDates } from 'components/common-form/hooks/useExpiryDates';
import { useFieldMetadata } from 'components/common-form/hooks/useFieldMetadata';
import { useFieldTranslationKey } from 'components/common-form/hooks/useFieldTranslationKey';
import { useFieldValidationError } from 'components/common-form/hooks/useFieldValidationError';
import { usePatchedFieldHighlight } from 'components/common-form/hooks/usePatchedFieldHighlight';
import type { DatePickerSize } from 'components/shared/DatePicker/DatePicker';
import {
  MonoDatePickerWithCustomPreset,
  MonoDatePickerWithDefaultPreset,
} from 'components/shared/DatePicker/MonoDatePicker';
import type { DateCalculation } from 'components/shared/DatePicker/utils';
import { BlockOrientationFormGroup } from 'components/shared/FormGroup';
import { format, parseISO } from 'date-fns';
import { useDateFnLocale, useSPIntl } from 'hooks/useIntl';
import { usePasteSingleDate } from 'hooks/usePasteSingleDate';
import type { IntlKey } from 'locales';
import type { DateFieldData, DateFieldMetadata } from 'models/forms/fields';
import { FC, useCallback, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getQuoteUnderlyings } from 'state/selectors';
import type { AppState } from 'state/state.model';
import type { WorkspaceType } from 'state/workspaces/model';
import { getDateOnlyFromIsoString } from 'utils/date';
import type { TranslatableContent } from 'utils/intl';
import { capitalizeFirstletter } from 'utils/string';

import type { ProductNodeBuilderProps } from '../ProductNodeBuilder.model';

export const DateField: FC<ProductNodeBuilderProps> = ({ node: { id }, className, workspaceType, tabId, tabIndex }) => {
  const translationKey = useFieldTranslationKey(id);
  const patchedFieldClassName = usePatchedFieldHighlight(id);

  return (
    <BlockOrientationFormGroup
      inputId={id}
      label={`form.${translationKey}` as any}
      workspaceType={workspaceType}
      className={patchedFieldClassName}
    >
      <FieldDatePicker id={id} tabId={tabId} className={className} tabIndex={tabIndex} workspaceType={workspaceType} />
    </BlockOrientationFormGroup>
  );
};
DateField.displayName = 'DateField';

type FieldDatePickerProps = {
  id: string;
  size?: DatePickerSize;
  className?: string;
  tabId?: string;
  hasError?: boolean;
  tabIndex?: number;
  workspaceType?: WorkspaceType;
};
export const FieldDatePicker: FC<FieldDatePickerProps> = ({
  id,
  size,
  className,
  tabId,
  hasError,
  tabIndex,
  workspaceType,
}) => {
  const { formatDisplayedDate } = useSPIntl();
  const locale = useDateFnLocale();
  const [value, setValue] = useData<DateFieldData>(id);
  const isFieldInvalid = useFieldValidationError(id)?.severity !== undefined;
  const isReadonly = useBlockOrFieldReadOnly(id);
  const {
    allowWeekend,
    isClearable,
    startDate,
    endDate,
    calendars = [],
    isReadOnly,
    useCustomPresets = false,
    customPresetDates,
    displayPresets = true,
  } = useFieldMetadata<DateFieldMetadata>(id) ?? {};
  const { isCellInactive, isFocused, lostInputBeforeEditing, onEditingKeyDown, isSelectAll } =
    useContext(BatchNavigationContext);
  const underlyings = useSelector((state: AppState) => getQuoteUnderlyings(state, tabId!));

  const blackoutDates = useSettlementBlackoutDates(calendars);

  const monthlyDatesCalendars = useMemo(() => {
    return [...new Set(underlyings?.filter(u => isNonEmpty(u.calendar?.id)).map(u => u.calendar))].filter(isDefined);
  }, [underlyings]);
  const calendarTypes = monthlyDatesCalendars.map(calendar => calendar.calendarType);
  const expiryDates = useExpiryDates(useCustomPresets ? calendarTypes : [], monthlyDatesCalendars);

  const onChange = useCallback(
    (newValue: Nullable<string>) => {
      if (newValue !== value) {
        setValue(newValue);
      }
    },
    [value, setValue],
  );

  const formattedDate = getDateOnlyFromIsoString(value);
  const onPaste = usePasteSingleDate(setValue);
  const readOnlyFormattedValue = isDefined(value) ? formatDisplayedDate(value) : '';

  const customPresetTranslationKeys: TranslatableContent[] | undefined = useMemo(() => {
    if (isDefined(customPresetDates) && isNonEmpty(Object.keys(customPresetDates))) {
      return Object.keys(customPresetDates)
        .map(customPresetDateKey => {
          if (isEmpty(customPresetDateKey)) {
            return undefined;
          }
          return { id: `date.presets.${customPresetDateKey}` as IntlKey };
        })
        .filter(isDefined);
    }

    if (isNotDefined(expiryDates) || isEmpty(expiryDates)) {
      return undefined;
    }
    return expiryDates
      .map(expiryDate => {
        if (isNotDefined(expiryDate)) {
          return undefined;
        }
        const parsedDate = parseISO(expiryDate);
        const translatedLabel = capitalizeFirstletter(format(parsedDate, 'MMM yyyy', { locale }));
        return { id: 'date.presets.custom' as IntlKey, values: { label: `${translatedLabel}` } };
      })
      .filter(isDefined);
  }, [customPresetDates, expiryDates, locale]);

  const customPresets: DateCalculation[] | undefined = useMemo(() => {
    if (isDefined(customPresetDates) && isNonEmpty(Object.values(customPresetDates))) {
      return Object.values(customPresetDates)
        .map(customPresetDate => {
          if (isNotDefined(customPresetDate)) {
            return undefined;
          }
          return ((_: Date) => parseISO(customPresetDate)) as unknown as DateCalculation;
        })
        .filter(isDefined);
    }

    if (isNotDefined(expiryDates)) {
      return undefined;
    }

    return expiryDates
      .map(expiryDate => {
        if (isNotDefined(expiryDate)) {
          return undefined;
        }
        return ((_: Date) => parseISO(expiryDate)) as unknown as DateCalculation;
      })
      .filter(isDefined);
  }, [customPresetDates, expiryDates]);

  const handleKeyDown = useCallback(
    ({ e, commit }: OnKeyDownArg) => {
      if (onEditingKeyDown) {
        onEditingKeyDown(e);
        switch (e.key) {
          case 'Enter':
          case 'Tab':
            commit();
            break;
          default:
            break;
        }
      }
    },
    [onEditingKeyDown],
  );

  if (isCellInactive || isReadOnly) {
    return (
      <input
        className="form-control pe-none"
        data-e2e={id + '-input'}
        id={id}
        name={id}
        readOnly={isReadOnly}
        type="text"
        value={readOnlyFormattedValue}
        tabIndex={tabIndex}
      />
    );
  }

  return isDefined(customPresetTranslationKeys) && isDefined(customPresets) ? (
    <MonoDatePickerWithCustomPreset
      className={classnames(className)}
      size={size}
      value={formattedDate as Nullable<string>}
      id={id}
      onChange={onChange}
      onPaste={onPaste}
      disabled={isReadonly}
      allowWeekend={allowWeekend}
      blackoutDates={blackoutDates}
      isClearable={isClearable ?? false}
      minDate={startDate}
      maxDate={endDate}
      hasError={isFieldInvalid || hasError === true}
      tabId={tabId}
      tabIndex={tabIndex}
      isFocused={isFocused}
      onKeyDown={handleKeyDown}
      workspaceType={workspaceType}
      inputValue={lostInputBeforeEditing}
      isSelectAll={isSelectAll}
      datePresetTranslations={customPresetTranslationKeys}
      datePresets={customPresets}
      hidePresets={!displayPresets}
    />
  ) : (
    <MonoDatePickerWithDefaultPreset
      className={classnames(className)}
      size={size}
      value={formattedDate as Nullable<string>}
      id={id}
      onChange={onChange}
      onPaste={onPaste}
      disabled={isReadonly}
      allowWeekend={allowWeekend}
      blackoutDates={blackoutDates}
      isClearable={isClearable ?? false}
      minDate={startDate}
      maxDate={endDate}
      hasError={isFieldInvalid || hasError === true}
      tabId={tabId}
      tabIndex={tabIndex}
      isFocused={isFocused}
      onKeyDown={handleKeyDown}
      workspaceType={workspaceType}
      inputValue={lostInputBeforeEditing}
      isSelectAll={isSelectAll}
      hidePresets={!displayPresets}
    />
  );
};
